import {
  Divider,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMemo } from 'react';

import { RoleAccordionBody } from '@/components/permissions/scopeAccordion';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import type { UpsertGroupMappingState } from './upsertGroupMappingFlow';

import { EntityBadge } from '../users/userBadge';

export const UpsertGroupMappingStep = (
  props: StepProps<UpsertGroupMappingState>
) => {
  const dal = useDAL();
  const { data: samlProvidersBody } = dal.saml.useList();
  const { data: idpGroupsBody } = dal.idpGroups.useList();

  const selectedProvider = useMemo(
    () =>
      samlProvidersBody?.samlIdentityProviders?.find(
        (x) => x.id === props.wizardState.idpId
      ),
    [samlProvidersBody, props.wizardState.idpId]
  );

  const roleToProjectsMappings = useMemo(
    () =>
      Object.fromEntries(
        props.wizardState.roleIds.map((roleId) => [roleId, {}])
      ),
    [props.wizardState.roleIds]
  );

  return (
    <>
      <StepContainer
        hideStepper
        onBackClick={props.back}
        canGoNext={() => {
          if (
            !props.wizardState.idpId ||
            !props.wizardState.providerGroupId ||
            !props.wizardState.roleIds.length
          ) {
            return false;
          }

          if (!props.wizardState.editMode) {
            const duplicate = (idpGroupsBody?.groups || []).some(
              (g) =>
                g.idpId === props.wizardState.idpId &&
                g.providerGroupId === props.wizardState.providerGroupId
            );
            if (duplicate) {
              return false;
            }
          }

          return true;
        }}
        nextButtonText={props.wizardState.editMode ? 'Save Changes' : 'Create'}
        backButtonText='Cancel'
        onNextClick={() => props.finish()}
      >
        <Stack gap='12px'>
          {props.wizardState.editMode && (
            <EntityBadge
              title={props.wizardState.providerGroupId}
              subtitle={selectedProvider?.providerName || ''}
              className='mx-[60px] pb-[12px]'
            />
          )}
          {!props.wizardState.editMode && (
            <>
              <Typography className='mx-[60px] font-semibold'>
                Group details
              </Typography>
              <Stack
                gap='16px'
                className='px-[60px] py-[12px] w-1/2'
                direction='row'
              >
                <FormControl size='small' className='w-1/2'>
                  <FormLabel>Group ID</FormLabel>
                  <TextField
                    value={props.wizardState.providerGroupId}
                    onChange={(event) =>
                      props.setWizardState((state) => ({
                        ...state,
                        providerGroupId: event.target.value,
                      }))
                    }
                  />
                </FormControl>
                <FormControl size='small' className='w-1/2'>
                  <FormLabel>SAML identity provider</FormLabel>
                  <Select
                    size='small'
                    disabled={!samlProvidersBody?.samlIdentityProviders?.length}
                    value={
                      (samlProvidersBody?.samlIdentityProviders &&
                        props.wizardState.idpId) ||
                      ''
                    }
                    onChange={(event) => {
                      props.setWizardState((state) => ({
                        ...state,
                        idpId: event.target.value,
                      }));
                    }}
                  >
                    {(samlProvidersBody?.samlIdentityProviders || []).map(
                      (saml) => (
                        <MenuItem key={saml.id} value={saml.id}>
                          {saml.providerName}
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </Stack>
            </>
          )}
          {!props.wizardState.editMode && (
            <>
              <Divider sx={{ mx: '-40px', my: '12px' }} />
              <Typography className='my-[12px] mx-[60px] font-semibold'>
                Permissions
              </Typography>
            </>
          )}
          <Stack gap='12px' className='ml-[60px]'>
            <Typography className='my-[24px]'>
              Edit the permissions this group has by assigning roles
            </Typography>
            <RoleAccordionBody
              roleToProjectsMappings={roleToProjectsMappings}
              onChange={(updated) =>
                props.setWizardState((state) => ({
                  ...state,
                  roleIds: Object.keys(updated),
                }))
              }
            />
          </Stack>
        </Stack>
      </StepContainer>
    </>
  );
};
